import type { RecipeVisibility } from '@recipes/contracts';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@recipes/ui';
import { Check, Copy, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { toastError } from '@/lib/errors';
import { useShareSettings, useUpdateShare } from './queries';

export interface ShareDialogProps {
  recipeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whether public sharing is enabled by the server (feature flag). */
  publicSharingEnabled: boolean;
}

const VISIBILITIES: RecipeVisibility[] = ['PRIVATE', 'HOUSEHOLD', 'UNLISTED', 'PUBLIC'];

export function ShareDialog({
  recipeId,
  open,
  onOpenChange,
  publicSharingEnabled,
}: ShareDialogProps) {
  const { t } = useTranslation();
  const settings = useShareSettings(recipeId, open);
  const update = useUpdateShare(recipeId);
  const [copied, setCopied] = useState(false);
  const shareUrl = settings.data?.shareUrl ?? null;
  const absoluteUrl = shareUrl
    ? shareUrl.startsWith('http')
      ? shareUrl
      : `${window.location.origin}${shareUrl}`
    : null;

  const copy = async () => {
    if (!absoluteUrl) return;
    try {
      await navigator.clipboard.writeText(absoluteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('recipes.share.copyFailed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>{t('recipes.share.title')}</DialogTitle>
          <DialogDescription>{t('recipes.share.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="share-visibility" className="mb-1 block">
              {t('recipes.share.visibility')}
            </Label>
            <Select
              value={settings.data?.visibility ?? 'PRIVATE'}
              disabled={settings.isPending || update.isPending}
              onValueChange={(v) =>
                update.mutate(
                  { visibility: v as RecipeVisibility, regenerateSlug: false },
                  { onError: (e) => toastError(e) },
                )
              }
            >
              <SelectTrigger id="share-visibility" data-testid="share-visibility">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VISIBILITIES.map((v) => (
                  <SelectItem key={v} value={v} disabled={v === 'PUBLIC' && !publicSharingEnabled}>
                    {t(`recipes.visibility.${v}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              {t(`recipes.share.hint.${settings.data?.visibility ?? 'PRIVATE'}`)}
            </p>
            {!publicSharingEnabled && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('recipes.share.publicDisabled')}
              </p>
            )}
          </div>
          {absoluteUrl && (
            <div>
              <Label htmlFor="share-url" className="mb-1 block">
                {t('recipes.share.link')}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="share-url"
                  readOnly
                  value={absoluteUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  data-testid="share-url"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void copy()}
                  aria-live="polite"
                >
                  {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
                  {copied ? t('recipes.share.copied') : t('recipes.share.copy')}
                </Button>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2"
                loading={update.isPending}
                onClick={() =>
                  update.mutate(
                    { visibility: settings.data!.visibility, regenerateSlug: true },
                    {
                      onSuccess: () => toast.success(t('recipes.share.regenerated')),
                      onError: (e) => toastError(e),
                    },
                  )
                }
              >
                <RefreshCw aria-hidden /> {t('recipes.share.regenerate')}
              </Button>
            </div>
          )}
          {settings.isError && <Alert variant="destructive">{t('recipes.share.loadError')}</Alert>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
